import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromTaksperrer } from "@/components/calculators/lineMappers";
import { taksperrerCalc } from "@/domain/calculators/carpenter";

export const Route = createFileRoute("/_authenticated/calculations/carpenter/taksperrer")({
  component: TaksperrerPage,
});

function TaksperrerPage() {
  const [byggLengde, setByggLengde] = useState<number | "">("");
  const [spenn, setSpenn] = useState<number | "">("");
  const [vinkel, setVinkel] = useState<number | "">(25);
  const [cc, setCc] = useState<number | "">(600);

  const r = useMemo(() => {
    if (byggLengde === "" || spenn === "" || vinkel === "" || cc === "") return null;
    return taksperrerCalc({
      bygg_lengde_m: Number(byggLengde),
      spennvidde_m: Number(spenn),
      takvinkel_grader: Number(vinkel),
      cc_mm: Number(cc),
    });
  }, [byggLengde, spenn, vinkel, cc]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Taksperrer" subtitle="Beregn sperrelengde og antall for saltak." />
      <CalcShell
        title="Inndata"
        advisory="Dimensjon på sperrer må bestemmes med tabell eller prosjektering (spennvidde + last)."
        result={r ? (
          <>
            <ResultCard
            title="Materialbehov"
            rows={[
              { label: "Sperrelengde",   value: `${fmt(r.sperre_lengde_m, 2)} m`, strong: true },
              { label: "Antall sperrer", value: `${fmt(r.antall_sperrer, 0)} stk (par på begge sider)` },
              { label: "Total løpemeter", value: `${fmt(r.total_lm, 1)} m` },
            ]}
            note="Spennvidde = hele husets bredde. Beregningen deler i to sider."
          />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Taksperrer`} lines={linesFromTaksperrer(r)} defaultLaborHours={Math.max(0, Math.round((r.antall_sperrer * 0.5) * 10) / 10)} />
            </div>
          </>
        ) : <div className="card p-5 text-sm text-muted-foreground">Fyll inn husets mål og takvinkel.</div>}
      >
        <Field label="Bygg­lengde (langs mønet)" unit="m"><NumberInput value={byggLengde} onChange={setByggLengde} /></Field>
        <Field label="Spennvidde (hus­bredde)" unit="m"><NumberInput value={spenn} onChange={setSpenn} /></Field>
        <Field label="Takvinkel" unit="°"><NumberInput value={vinkel} onChange={setVinkel} /></Field>
        <Field label="c/c sperrer" unit="mm"><NumberInput value={cc} onChange={setCc} /></Field>
      </CalcShell>
    </>
  );
}
